import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";

/*
 * Shared spine of the two bookplate variants (§6): the double rule is drawn as
 * an inset `outline` so the `border` stays available for the pressed state, and
 * the stamp-in-ink behaviour comes from `aria-pressed`, never from a class the
 * page sets. `plate` adds the italic chip face, `folio` the upright page number.
 *
 * The plate is the one control that still hovers on `--accent`: its rest state
 * already is `bg-muted`, so a muted hover would be no feedback at all, and
 * `--accent` is the only token one step stronger in *both* themes (§7.8 gives
 * it exactly this job — a hover state, not a brand colour).
 */
const PLATE =
  "rounded-xs border-foreground/30 bg-muted font-serif text-center wrap-break-word text-foreground outline-1 -outline-offset-2 outline-foreground/20 duration-200 active:translate-y-px motion-reduce:transition-none hover:bg-accent hover:text-accent-foreground hover:outline-foreground/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-foreground aria-pressed:outline-primary-foreground/30 aria-pressed:hover:bg-primary/90 aria-pressed:hover:text-primary-foreground";

/*
 * Local contract (DESIGN.md §6): the base carries `cursor-pointer`. Tailwind v4's
 * preflight sets `cursor: default` on every `<button>`, so without this the app's
 * controls show a text arrow and read as inert; the sites that previously re-added
 * the pointer by hand no longer have to. `disabled:pointer-events-none` below keeps
 * a disabled control from claiming it.
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none cursor-pointer focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border bg-background shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        /* Local contract (DESIGN.md §6): ghost with quiet resting text, for
           inline and dense actions (expand/collapse, reply, meta controls). */
        quiet:
          "text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        /* Local contract (DESIGN.md §6): the success-tone mirror of
           `destructive` — for positive filters and confirmed states. */
        success:
          "bg-success/10 text-success hover:bg-success/20 focus-visible:border-success/40 focus-visible:ring-success/20 dark:bg-success/20 dark:hover:bg-success/30 dark:focus-visible:ring-success/40",
        /* Local contract (DESIGN.md §6): borderless destructive for row/list
           actions where the tinted `destructive` fill would be too loud. */
        "ghost-destructive":
          "text-destructive hover:bg-destructive/10 hover:text-destructive aria-expanded:bg-destructive/10 dark:hover:bg-destructive/20",
        /* Local contract (DESIGN.md §6): destructive outlined for filter
           chips / clear actions. */
        "outline-destructive":
          "border-destructive/30 bg-transparent text-destructive shadow-xs hover:bg-destructive/10 hover:text-destructive",
        link: "text-primary underline-offset-4 hover:underline",
        /* Local contract (DESIGN.md §6): the row/list family — same `--muted`
          hover fill as `ghost`/`quiet` (one hover token for controls), but at
          label weight rather than control weight, because a row is not a
          button caption. `DropdownMenuItem` still fills on `--accent`: it is
          vendored and kept close to upstream. */
        menu: "font-normal hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        "menu-quiet":
          "font-normal text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        "menu-destructive":
          "font-normal text-muted-foreground hover:bg-destructive/10 hover:text-destructive aria-expanded:bg-destructive/10",
        /* Local contract (DESIGN.md §6): borderless inline action that changes
           colour only — no fill at all, for actions set into running text or a
           caption line. `justify-start` because it hugs its own label. */
        bare: "font-normal justify-start text-muted-foreground hover:bg-transparent hover:text-foreground aria-expanded:bg-transparent aria-expanded:text-foreground",
        "bare-destructive":
          "font-normal justify-start text-muted-foreground hover:bg-transparent hover:text-destructive",
        /* Local contract (DESIGN.md §6): `bare` in the voice of a printed
           marginal note — the italic serif of a description or imprint line, so
           an inline action set into a library surface reads as annotation, not
           as a control. One of the three sanctioned serif controls, with
           `plate`/`folio`; text size stays the size's job. */
        "bare-note":
          "font-serif font-normal italic justify-start text-muted-foreground hover:bg-transparent hover:text-foreground aria-expanded:bg-transparent aria-expanded:text-foreground",
        /* Local contract (DESIGN.md §6): the full-width bar that opens a
           disclosure or accordion section. It rests on the muted surface, so it
           reads as part of the panel, and stays filled while its section is
           open — `aria-expanded` is that state. The `menu` family is the wrong
           tool here: it fills only on hover, so an open section looks closed. */
        section:
          "font-normal bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        /* Local contract (DESIGN.md §6): the selectable row/card of a picker —
           rest `bg-card`, selected `bg-primary/10` under a `border-primary/50`
           rule, state read off `aria-pressed` the way the nav bookmark reads
           `aria-current`. Replaces the per-page `isSelected` class ternaries. */
        selectable:
          "font-normal justify-start border-border bg-card text-left hover:border-primary/40 hover:bg-muted hover:text-foreground aria-pressed:border-primary/50 aria-pressed:bg-primary/10 aria-pressed:text-foreground aria-pressed:hover:bg-primary/15",
        /* Local contract (DESIGN.md §6): the drawer tab that hangs on the
           viewport edge. Height and `fixed`/`translate` are layout, so the page
           still sets those; ink, rule and shadow are the variant's. */
        "edge-tab":
          "rounded-l-md bg-primary/80 text-primary-foreground shadow-lg hover:bg-primary/90",
        /* Local contract (DESIGN.md §6): the one inverted control — a floating
           chip that sits over content and must read as UI, not as prose. */
        invert:
          "bg-foreground text-background shadow-lg hover:bg-foreground/90 hover:text-background",
        /* Local contract (DESIGN.md §6): the control set *into* a filled ribbon —
           the header's action bookmark (components/lore/text-header-actions.tsx)
           and anything else whose surface is a solid `--primary` cloth. It owns
           three things the ribbon's parent cannot give it: a transparent rest fill
           so the cloth shows through unbroken, a hover step in
           `--primary-foreground` (the only token that reads as a change against
           the fill in *both* themes — `hover:bg-primary/80` over a `bg-primary`
           parent is the same colour), and a focus mark drawn inset, because the
           notch's `clip-path` cuts an outer ring off. Square, because a segment of
           a ribbon is a cross-section of cloth, not a chip. */
        ribbon:
          "rounded-none bg-transparent text-primary-foreground hover:bg-primary-foreground/15 focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-foreground",
        /* Local contract (DESIGN.md §6): the rollable block's opening CTA — a
           full-width bar on the card surface that the reader clicks to roll a
           d20. It rests on `--card` under the input rule and answers the pointer
           in the die's own accent (`--dice-body`, theme-independent per §3): the
           rule tints and the `shadow-dice` glow lifts off the bar. `selectable`
           is the wrong tool here — a picker row reads its state off
           `aria-pressed`, and this control has no selected state. */
        dice: "rounded-lg border-input bg-card text-card-foreground hover:border-dice-body/50 hover:shadow-dice motion-safe:duration-300",
        plate: `${PLATE} italic`,
        folio: PLATE,
      },
      size: {
        default:
          "h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
        lg: "h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md",
        "icon-lg": "size-10",
        /* A 16px control for the dots that live inside another component's box
           (a badge's remove affordance, a table cell's insert handle). Smaller
           than `icon-xs`, and it drops the gap a two-icon control needs. */
        "icon-2xs":
          "size-4 gap-0 px-0 text-xs [&_svg:not([class*='size-'])]:size-3",
        /* Local contract (DESIGN.md §6): row densities. `row` is a single-line
           menu/settings row (`DropdownMenuItem`'s box), `block` is a multi-line
           selectable card row — it releases the base's `shrink-0` and
           `whitespace-nowrap`, which a wrapping block cannot keep. */
        row: "h-auto w-full justify-start gap-2 px-2 py-2 whitespace-normal",
        block:
          "h-auto w-full shrink justify-start gap-2 p-3 whitespace-normal text-left",
        /* The die CTA's box, paired with the `dice` variant: no fixed height,
           the rhythm a 5rem die face needs, and `overflow-hidden` so the blurred
           halo is cut to the bar's own rounded rect instead of bleeding over the
           paragraph. */
        dice: "h-auto w-full shrink gap-4 overflow-hidden px-5 py-4",
        plate: "h-auto min-h-7 gap-1.5 px-3 py-1 text-xs whitespace-normal",
        /* The same plate at sentence size — what the sidebar's entry-point and
           filter plates use when a label needs to read as body text, not as a
           micro caption. */
        "plate-lg":
          "h-auto min-h-9 gap-2 px-3 py-1.5 text-sm whitespace-normal",
        /* Local contract (DESIGN.md §6): the two flush-inline boxes for the
           `bare` family — an action set into a line of text or a caption row.
           They carry no horizontal padding, because an inline action has to
           start where its sentence does; `xs`/`sm` indent it by design. */
        inline: "h-auto justify-start gap-1.5 px-0 whitespace-normal",
        "inline-xs":
          "h-auto justify-start gap-1 px-0 text-xs whitespace-normal [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  round = false,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /* Local contract (DESIGN.md §6): the circular box for icon-only controls —
       a radius is shape, so a page cannot ask for it in `className`. Applies
       after the variant/size string so it wins the merge. */
    round?: boolean;
  }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        round && "rounded-full",
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
